/**
 * OwnershipFields - Ownership form fields
 *
 * Fields: owner_client_id, operator_client_id, owner_name
 */

import { useMemo } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useClients } from '@/hooks/useClients'
import { getClientDisplayName } from '@/types/clients'
import type { AircraftFormValues } from '../aircraft-form-schema'

interface OwnershipFieldsProps {
  form: UseFormReturn<AircraftFormValues>
  disabled?: boolean
}

export function OwnershipFields({ form, disabled = false }: OwnershipFieldsProps) {
  const { data: clients } = useClients()

  const clientOptions = useMemo(
    () =>
      (clients ?? []).map((client) => ({
        value: client.id,
        label: getClientDisplayName({
          client_type: client.client_type,
          company_name: client.company_name,
          first_name: client.first_name,
          last_name: client.last_name,
        }),
      })),
    [clients]
  )

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="owner_client_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Owner (Client)</FormLabel>
            <FormControl>
              <SearchableSelect
                options={clientOptions}
                value={field.value}
                onValueChange={field.onChange}
                placeholder="Select owner"
                searchPlaceholder="Search clients..."
                emptyMessage="No clients found."
                allowClear
                disabled={disabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="operator_client_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Operator (Client)</FormLabel>
            <FormControl>
              <SearchableSelect
                options={clientOptions}
                value={field.value}
                onValueChange={field.onChange}
                placeholder="Select operator"
                searchPlaceholder="Search clients..."
                emptyMessage="No clients found."
                allowClear
                disabled={disabled}
              />
            </FormControl>
            <FormDescription>If different from owner</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="owner_name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Owner Name (Text)</FormLabel>
            <FormControl>
              <Input
                {...field}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value || null)}
                placeholder="If not in client list"
                disabled={disabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  )
}
